import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { MenuItem } from "@mui/material";
import ConfirmationModel from "components/ConfirmationModel";
import Field from "components/Field";
import { FormContext } from "components/FormContext";
import { Formik } from "formik";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getClients } from "store/actions/clients";
import { setProgramData } from "store/actions/program";
import { formatDate } from "utils/common";
import { trackEvent } from "utils/analytics";

const SendProgramModal = ({
  showSendProgramModal,
  handleDraftSubmit,
  setShowSendProgramModal,
  isFitnessProgram = false,
  isIndependentSession = false,
}) => {
  const { formikRef, handleSubmit } = useContext(FormContext);
  const navigate = useNavigate();

  const clientList = useSelector((state) => state.clients.clients);
  const nutritionData = useSelector((state) => state.nutritions.nutritionData);
  const programData = useSelector((state) => state.programs.programData);
  const dispatch = useDispatch();
  const currentRef = useRef(null);
  const [programDetails, setProgramDetails] = useState({});
  const [step, setStep] = useState("form");
  const [sentToUserId, setSentToUserId] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    if (isFitnessProgram) {
      setProgramDetails(programData);
    } else if (isIndependentSession) {
      const sessionDetails = formikRef?.current?.values?.week;
      setProgramDetails(sessionDetails ? sessionDetails[0][0] : []);
    } else {
      setProgramDetails(nutritionData);
    }
  }, [isFitnessProgram, isIndependentSession && formikRef?.current]);

  useEffect(() => {
    if (clientList.length === 0) {
      dispatch(getClients());
    }
  }, []);

  const handleClose = () => {
    setShowSendProgramModal(false);
    setStep("form");
    setSentToUserId(null);
  };

  const programsPath = isFitnessProgram
    ? "/fitness/programs"
    : isIndependentSession
    ? "/fitness/sessions"
    : "/nutrition/programs";

  const successBody = (
    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={2}>
      <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "success.main" }} />
      <Typography variant="h6" fontWeight={700} mt={2} mb={1} fontSize={20}>
        Envoyé avec succès !
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Le programme a bien été assigné à votre client.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Vous pouvez consulter le profil du client ou revenir à la liste de vos programmes.
      </Typography>
      <Stack direction="row" gap={2} width="100%">
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          onClick={() => {
            handleClose();
            if (sentToUserId) navigate(`/clients/${sentToUserId}`);
          }}
        >
          Voir le client
        </Button>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => {
            handleClose();
            navigate(programsPath);
          }}
        >
          Voir mes programmes
        </Button>
      </Stack>
    </Box>
  );

  return (
    <ConfirmationModel
      isOpen={showSendProgramModal}
      confirmButtonColor="primary"
      confirm="Envoyer"
      back="Sauvegarder en brouillon"
      title={
        step === "success"
          ? ""
          : isIndependentSession
          ? "Envoyer la séance"
          : "Envoyer la programme"
      }
      showActions={step !== "success"}
      hideBackButton={
        step === "success" ||
        (id &&
          (programDetails?.status === "Actif" ||
            programDetails?.status === "Modèle"))
      }
      body={
        step === "success" ? (
          successBody
        ) : (
          <>
            {isIndependentSession ? (
              <Grid container fontSize={14} mt={3} mb={3}>
                <Grid item sm={6}>
                  Titre de la séance :
                </Grid>
                <Grid item fontWeight={700} textAlign="right" sm={6}>
                  {programDetails?.name || "-"}
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid container fontSize={14} mt={3} mb={1}>
                  <Grid item sm={6}>
                    Nom de programme:
                  </Grid>
                  <Grid item fontWeight={700} textAlign="right" sm={6}>
                    {programDetails?.name || "-"}
                  </Grid>
                </Grid>
                <Grid container fontSize={14} mb={1}>
                  <Grid item sm={6}>
                    Date de début:
                  </Grid>
                  <Grid fontWeight={700} textAlign="right" item sm={6}>
                    {formatDate(new Date(programDetails?.start_date) || "")}
                  </Grid>
                </Grid>
                <Grid container fontSize={14} mb={3}>
                  <Grid item sm={6}>
                    Durée:
                  </Grid>
                  <Grid fontWeight={700} textAlign="right" item sm={6}>
                    {programDetails?.program_weeks} semaines
                  </Grid>
                </Grid>
              </>
            )}
            <Formik
              innerRef={currentRef}
              enableReinitialize={true}
              initialValues={{ user_id: programDetails?.user_id ?? "" }}
            >
              <Field
                type="select"
                label="Ajouter un client"
                placeholder="Ajouter un client"
                name="user_id"
                notched={true}
                options={clientList}
                disabled={id && programDetails?.status === "Actif"}
              >
                {clientList?.map((item) => (
                  <MenuItem value={item._id} key={item._id}>
                    {`${item.first_name || "-"} ${item.last_name}`}
                  </MenuItem>
                ))}
              </Field>
            </Formik>
          </>
        )
      }
      maxWidth="sm"
      handleClose={handleClose}
      onSecondarySubmit={() => handleDraftSubmit()}
      onConfirm={async () => {
        const userId = currentRef.current?.values?.user_id;
        if (isFitnessProgram) {
          dispatch(
            setProgramData({
              ...programData,
              status: "Actif",
              user_id: userId,
            })
          );
        }
        formikRef.current?.setFieldValue("status", "Actif");
        formikRef.current?.setFieldValue("user_id", userId);
        await handleSubmit();
        trackEvent('Programs', isFitnessProgram ? 'program_sent' : isIndependentSession ? 'session_created' : 'nutrition_sent');
        setSentToUserId(userId);
        setStep("success");
      }}
    />
  );
};

export default SendProgramModal;
